'use client';

import { LayoutDashboard, Menu, Moon, Sun, X } from 'lucide-react';
import NextLink from 'next/link';
import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import { useState } from 'react';

import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@/components/ui/navigation-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { BookingLink } from '@/features/landing/components/booking-link';
import { LanguageSwitcher } from '@/features/landing/components/language-switcher';
import { ModeToggle } from '@/features/landing/components/mode-toggle';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { Link } from '@/i18n/routing';
import { LANDING_LOCALES } from '@/lib/constants';

// Smooth scroll function
const smoothScrollTo = (targetId: string | number) => {
  if (typeof targetId === 'number') {
    window.scrollTo({ top: targetId, behavior: 'smooth' });
  } else if (targetId.startsWith('#')) {
    const element = document.querySelector(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
};

// Helper function to check if current path is a landing page
const isLandingPage = (pathname: string): boolean => {
  const localePaths = LANDING_LOCALES.map((locale) => `/${locale}`);
  return pathname === '/' || localePaths.includes(pathname);
};

export function LandingNavbar() {
  const t = useTranslations('landing.navbar');
  const tAuth = useTranslations('auth');
  const [isOpen, setIsOpen] = useState(false);
  const { setTheme, theme } = useTheme();
  const { isAuthenticated, loading: authLoading } = useIsAuthenticated();

  const navigationItems = [
    { name: t('items.home'), href: '/' },
    { name: t('items.problem'), href: '/#problem' },
    { name: t('items.solution'), href: '/#solution' },
    { name: t('items.benefits'), href: '/#benefits' },
    { name: t('items.blog'), href: '/#blog' },
    { name: t('items.pilot'), href: '/#pilot' },
    { name: t('items.faq'), href: '/#faq' },
    { name: t('items.contact'), href: '/#contact' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <Link href="/" className="flex items-center cursor-pointer">
            <Logo variant="auto" width={120} height={32} />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <NavigationMenu className="hidden xl:flex">
          <NavigationMenuList>
            {navigationItems.map((item) => (
              <NavigationMenuItem key={item.name}>
                <NavigationMenuLink
                  asChild
                  className="group inline-flex h-10 w-max items-center justify-center px-4 py-2 text-sm font-medium transition-colors hover:text-primary focus:text-primary focus:outline-none cursor-pointer"
                  onClick={(e: React.MouseEvent) => {
                    const pathname = window.location.pathname;
                    const isCurrentLandingPage = isLandingPage(pathname);
                    const targetId = item.href.replace(/^\//, '');

                    if (targetId.startsWith('#')) {
                      if (isCurrentLandingPage) {
                        e.preventDefault();
                        smoothScrollTo(targetId);
                      }
                    } else if (item.href === '/' && isCurrentLandingPage) {
                      e.preventDefault();
                      smoothScrollTo(0);
                    }
                  }}
                >
                  <Link href={item.href}>{item.name}</Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>

        {/* Desktop CTA */}
        <div className="hidden xl:flex items-center space-x-2">
          <LanguageSwitcher supportedLocales={LANDING_LOCALES} />
          <ModeToggle variant="ghost" />
          {!authLoading && isAuthenticated ? (
            <Button asChild className="cursor-pointer">
              <NextLink href="/app">
                <LayoutDashboard className="h-4 w-4" />
                {tAuth('actions.goToDashboard')}
              </NextLink>
            </Button>
          ) : (
            <>
              <Button variant="outline" asChild className="cursor-pointer">
                <NextLink href="/auth/employee-login" rel="nofollow">
                  {t('actions.employeeLogin')}
                </NextLink>
              </Button>
              <Button asChild className="cursor-pointer">
                <BookingLink location="navbar_desktop">{t('actions.apply')}</BookingLink>
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="xl:hidden">
            <Button variant="ghost" size="icon" className="cursor-pointer">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-full sm:w-[400px] p-0 gap-0 [&>button]:hidden overflow-hidden flex flex-col"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <SheetHeader className="space-y-0 p-4 pb-2 border-b">
                <div className="flex items-center gap-2">
                  <Logo variant="auto" width={100} height={26} />
                  <SheetTitle className="sr-only">BiteClub Menu</SheetTitle>
                  <div className="ml-auto flex items-center gap-2">
                    <LanguageSwitcher supportedLocales={LANDING_LOCALES} />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                      className="cursor-pointer h-8 w-8"
                    >
                      <Moon className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                      <Sun className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsOpen(false)}
                      className="cursor-pointer h-8 w-8"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </SheetHeader>

              {/* Navigation Links */}
              <div className="flex-1 overflow-y-auto">
                <nav className="p-6 space-y-1">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      className="flex items-center px-4 py-3 text-base font-medium rounded-lg transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
                      onClick={(e) => {
                        setIsOpen(false);
                        const pathname = window.location.pathname;
                        const isCurrentLandingPage = isLandingPage(pathname);
                        const targetId = item.href.replace(/^\//, '');

                        if (targetId.startsWith('#')) {
                          e.preventDefault();
                          if (isCurrentLandingPage) {
                            setTimeout(() => smoothScrollTo(targetId), 100);
                          } else {
                            window.location.href = `/${targetId}`;
                          }
                        }
                      }}
                    >
                      {item.name}
                    </Link>
                  ))}
                </nav>
              </div>

              {/* Footer Actions */}
              <div className="border-t p-6 space-y-4">
                {/* Primary Actions */}
                <div className="space-y-3">
                  {!authLoading && isAuthenticated ? (
                    <Button size="lg" asChild className="w-full cursor-pointer">
                      <NextLink href="/app" onClick={() => setIsOpen(false)}>
                        <LayoutDashboard className="h-4 w-4" />
                        {tAuth('actions.goToDashboard')}
                      </NextLink>
                    </Button>
                  ) : (
                    <>
                      <Button size="lg" asChild className="w-full cursor-pointer">
                        <BookingLink location="navbar_mobile" onClick={() => setIsOpen(false)}>
                          {t('actions.apply')}
                        </BookingLink>
                      </Button>

                      <Button variant="outline" size="lg" asChild className="w-full cursor-pointer">
                        <NextLink href="/auth/employee-login" rel="nofollow">
                          {t('actions.employeeLogin')}
                        </NextLink>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
